import { FlagsModule } from '@seddi/ui-feature-flags';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { downloadImage } from '../../actions/captureImage';
import { getBaseScene } from '../../actions/setScene';
import { setViewerRef } from '../../store/appRefsSlice';
import { getScene, setScene, updateScene } from '../../store/appSlice';
import type { TRootState } from '../../store/appStateTypes';
import { HEADWEAR_CAMERA_ANGLE_OFFSET, computeSceneLookAt } from '../../viewer/sceneLookAt';
import { createPieceNameToEntityDict } from '../../viewer/sceneUtils';
import type { LogoRegion } from '../../viewer/viewerScene';
import * as viewerScene from '../../viewer/viewerScene';

const featureFlagsDef = {
  depthPeelingLayers: 0,
  disableTransparencyAngleBasedCompensation: false,
  disableTransparencyFlatCompensation: false,
  mipMapsGaussianBlur: false,
  parallax: false,
  premultipliedAlphaLogos: true,
  wireframeFabrics: false,
} as const;

const visibleDef = {
  avatar: false,
  background: false,
  garmentHem: true,
  garmentPiece: true,
  garmentSeam: true,
  ground: false,
  logos: true,
} as const;

export const useAppViewer = (showGraphicsPanel = false) => {
  const dispatch = useDispatch();
  const sceneIsLoading = useSelector((state: TRootState) => state.app.sceneIsLoading);
  const sceneVersion = useSelector((state: TRootState) => state.app.sceneVersion);
  const stylePreviewUrl = useSelector((state: TRootState) => state.app.stylePreviewUrl);
  const styleNotFoundMessage = useSelector((state: TRootState) => state.app.styleNotFoundMessage);
  const styleType = useSelector((state: TRootState) => state.app.styleType);
  const mockupLogos = useSelector((state: TRootState) => state.app.mockupLogos);
  const styleRegions = useSelector((state: TRootState) => state.app.styleRegions);
  const mockupHideTearAwayLabel = useSelector(
    (state: TRootState) => state.app.mockupHideTearAwayLabel,
  );
  const selectedGraphicId = useSelector((state: TRootState) => state.app.selectedGraphicId);
  const hoverGraphicId = useSelector((state: TRootState) => state.app.hoverGraphicId);

  const viewerRef = useRef<any>(null);
  const lastFramedSceneVersionRef = useRef<number | null>(null);
  const adapterRef = useRef(viewerScene.create());
  const [localSceneVersion, setLocalSceneVersion] = useState(0);
  const [waitingForFirstFrame, setWaitingForFirstFrame] = useState(false);

  const logoRegions = useMemo(() => {
    const baseScene = getBaseScene();
    if (!baseScene || !styleRegions.length) return [];
    const pieceNameToEntityId = createPieceNameToEntityDict(baseScene);
    return styleRegions.map(r => ({
      ...r,
      entityId: pieceNameToEntityId[r.pieceName] ?? '',
    }));
  }, [styleRegions]);

  useEffect(() => {
    // communicate viewerRef through state
    dispatch(setViewerRef(viewerRef));
    // clear viewerRef state
    return () => {
      dispatch(setViewerRef(null));
    };
  }, [dispatch]);

  // Set style scene and regions when base scene becomes available.
  // baseScene is set in the same flow as styleRegions (setSceneFromStyleAggregatorData),
  // so we don't gate on sceneIsLoading - avoids loading never ending when waitForSceneReady hangs.
  useEffect(() => {
    const baseScene = getBaseScene();
    if (!baseScene) return;

    const adapter = adapterRef.current;
    viewerScene.setStyleScene(adapter, baseScene, mockupHideTearAwayLabel);

    if (!styleRegions.length) {
      setLocalSceneVersion(v => v + 1);
      return;
    }

    const pieceNameToEntityId = createPieceNameToEntityDict(baseScene);
    const logoRegions: LogoRegion[] = styleRegions.map(r => ({
      ...r,
      entityId: pieceNameToEntityId[r.pieceName] ?? '',
    }));

    viewerScene.setStyleRegions(adapter, logoRegions);
    setLocalSceneVersion(v => v + 1);
  }, [styleRegions, mockupHideTearAwayLabel]);

  // Reconcile logos when mockupLogos change.
  useEffect(() => {
    const baseScene = getBaseScene();
    if (!baseScene) return;

    const adapter = adapterRef.current;
    viewerScene.reconcileLogos(adapter, mockupLogos);
    setLocalSceneVersion(v => v + 1);
  }, [mockupLogos]);

  // Reconcile selection (style region border) when selected graphic changes.
  useEffect(() => {
    if (!showGraphicsPanel) return;
    const baseScene = getBaseScene();
    if (!baseScene) return;

    const selectedLogo = selectedGraphicId
      ? mockupLogos.find(l => l.id === selectedGraphicId)
      : undefined;

    const adapter = adapterRef.current;
    viewerScene.reconcileSelection(adapter, selectedLogo).then(() => {
      setLocalSceneVersion(v => v + 1);
    });
  }, [showGraphicsPanel, selectedGraphicId, mockupLogos]);

  // Reconcile hover (logo border) when hover or selection changes.
  useEffect(() => {
    if (!showGraphicsPanel) return;
    const baseScene = getBaseScene();
    if (!baseScene) return;

    const hoverLogo = hoverGraphicId ? mockupLogos.find(l => l.id === hoverGraphicId) : undefined;
    const selectedLogo = selectedGraphicId
      ? mockupLogos.find(l => l.id === selectedGraphicId)
      : undefined;

    const logoToShowBorder = hoverLogo ?? selectedLogo;
    const useSelectedColor = !hoverLogo && !!selectedLogo;

    const adapter = adapterRef.current;
    viewerScene.reconcileHover(adapter, logoToShowBorder, useSelectedColor).then(() => {
      setLocalSceneVersion(v => v + 1);
    });
  }, [showGraphicsPanel, hoverGraphicId, selectedGraphicId, mockupLogos]);

  // Clear hover and selection overlays when graphics panel is hidden.
  useEffect(() => {
    if (showGraphicsPanel) return;
    const baseScene = getBaseScene();
    if (!baseScene) return;

    const adapter = adapterRef.current;
    viewerScene.reconcileHover(adapter, undefined, false).then(() => {
      setLocalSceneVersion(v => v + 1);
    });
    viewerScene.reconcileSelection(adapter, undefined).then(() => {
      setLocalSceneVersion(v => v + 1);
    });
  }, [showGraphicsPanel]);

  // Update composed scene and propagate to viewer when sceneVersion changes.
  useEffect(() => {
    const adapter = adapterRef.current;
    viewerScene.updateScene(adapter);
    if (adapter.scene) {
      setScene(adapter.scene);
      dispatch(updateScene());
    }
  }, [localSceneVersion, dispatch]);

  const config = useMemo(() => {
    const useStitchesDecals = FlagsModule.isEnabled('use_stitches_decals');
    return { frustumCulling: false, useStitchesDecals };
  }, []);

  const scene = useMemo(() => {
    return getScene();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sceneVersion is intended to memoize the new scene
  }, [sceneVersion]);

  const featureFlags = featureFlagsDef;
  const visible = visibleDef;

  const handleDownloadImage = useCallback(() => {
    if (viewerRef.current) {
      downloadImage(viewerRef.current, 'image/webp');
    }
  }, []);

  // Clear waitingForFirstFrame when viewer has rendered a frame (avoids empty-page flash).
  const handleUpdateWithFirstFrame = useCallback(() => {
    setWaitingForFirstFrame(prev => (prev ? false : prev));
  }, []);

  // Callback that fires after each render completes.
  const handleUpdate = useCallback(() => {
    handleUpdateWithFirstFrame();
  }, [handleUpdateWithFirstFrame]);

  const frameScene = useCallback(
    (options?: { force?: boolean }) => {
      const { force = false } = options ?? {};

      if (!scene || sceneIsLoading) {
        return;
      }

      if (!force && lastFramedSceneVersionRef.current === sceneVersion) {
        return;
      }

      const viewer = viewerRef.current;
      if (!viewer) {
        return;
      }

      const canvas = viewer.canvasRef?.current as HTMLCanvasElement | null | undefined;
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        return;
      }

      const aspectRatio = canvas.width / canvas.height;
      const angleY = styleType?.toLowerCase() === 'headwear' ? HEADWEAR_CAMERA_ANGLE_OFFSET : 0;
      const camera = computeSceneLookAt(scene, aspectRatio, angleY);

      if (!camera) {
        return;
      }

      const tool = viewer.tool as any;
      tool.lookAt(camera.origin, camera.target);
      // set zoommax distance to initial camera distance
      if (typeof tool.setMaxDistance === 'function') {
        tool.setMaxDistance(camera.distance);
      }
      lastFramedSceneVersionRef.current = sceneVersion;
    },
    [scene, sceneIsLoading, sceneVersion, styleType],
  );

  const prevSceneIsLoadingRef = useRef<boolean | null>(null);
  useEffect(() => {
    const wasLoading = prevSceneIsLoadingRef.current;
    prevSceneIsLoadingRef.current = sceneIsLoading;

    if (wasLoading === true && sceneIsLoading === false) {
      setWaitingForFirstFrame(true);
      // Defer to next tick so scene composition effects have run
      const id = setTimeout(() => frameScene(), 0);
      return () => clearTimeout(id);
    }
  }, [sceneIsLoading, frameScene]);

  const handleCameraReset = useCallback(() => {
    frameScene({ force: true });
  }, [frameScene]);

  const handleZoomIn = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    const tool = viewer.tool as any;
    if (typeof tool.zoomIn === 'function') {
      tool.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    const tool = viewer.tool as any;
    if (typeof tool.zoomOut === 'function') {
      tool.zoomOut();
    }
  }, []);

  return {
    config,
    dispatch,
    featureFlags,
    handleDownloadImage,
    handleUpdate,
    handleCameraReset,
    handleZoomIn,
    handleZoomOut,
    hoverGraphicId,
    logoRegions,
    mockupLogos,
    scene,
    sceneIsLoading,
    selectedGraphicId,
    stylePreviewUrl,
    styleNotFoundMessage,
    viewerRef,
    visible,
    waitingForFirstFrame,
  };
};
